import { EventHandler, HandlerCollection } from '../../../Utils/mx-handler.collection';

export const enum OperationStatus {
	Started = 0,
	Updated = 1,
	Finished = 2,
	Canceling = 3,
	Canceled = 4,
}

export type ProcessItemOptions = {
	Id: string;
	OperationName: string;
	StartTime: Date;
	InitiatedBy: string;
	StatusDetails: string;
	Cancelable: boolean;
	CancellationUrl: string;
	IsExternal: boolean;
	OperationStatus: number;
	Data: unknown;
	Progress: number;
	FinishTime?: Date;
};

export class ProcessItem {
	private readonly timeout = 20 * 1000;
	private readonly events = {
		progressUpdated: new HandlerCollection<number>(),
		processStatusUpdated: new HandlerCollection<OperationStatus>(),
		processCanceling: new HandlerCollection<void, Promise<boolean>>(),
		processCanceled: new HandlerCollection(),
		processFailed: new HandlerCollection(),
	};
	private _id = '';
	private _progress = 0;
	private _canceled = false;
	private _failed = false;
	private _operationStatus = OperationStatus.Started;
	public longRunning = false;
	public operationName: string;
	public startTime: Date;
	public initiatedBy: string;
	public statusDetails: string;
	public cancelable: boolean;
	public cancellationUrl: string;
	public isExternal: boolean;
	public data: unknown;
	public finishTime?: Date;
	constructor(options: Partial<ProcessItemOptions>) {
		this.id = options.Id || this._id;
		this.operationName = options.OperationName || '';
		this.startTime = options.StartTime || new Date();
		this.initiatedBy = options.InitiatedBy || '';
		this.statusDetails = options.StatusDetails || '';
		this.cancelable = options.Cancelable || false;
		this.cancellationUrl = options.CancellationUrl || '';
		this.operationStatus = options.OperationStatus || this._operationStatus;
		this.isExternal = options.IsExternal || false;
		this.data = options.Data || {};
		this.progress = options.Progress || this._progress;
		this.finishTime = options.FinishTime || undefined;
	}

	public get id(): string {
		return this._id;
	}

	public set id(id: string) {
		if (!id) {
			throw new Error('Process Id is empty');
		}
		this._id = id;
	}

	public get isCanceled(): boolean {
		return this._canceled;
	}

	public get operationStatus(): OperationStatus {
		return this._operationStatus;
	}

	public set operationStatus(status: OperationStatus) {
		switch (status) {
			case OperationStatus.Started:
				this.startTime = new Date();
				break;
			case OperationStatus.Finished:
				this.finishTime = new Date();
				break;
			default:
				this.finishTime = undefined;
				break;
		}
		if (this._operationStatus !== status) {
			this._operationStatus = status;
			this.events.processStatusUpdated.execute(status);
		}
	}

	public get progress(): number {
		return this._progress;
	}

	public set progress(progress: number) {
		if (progress < 0 || progress > 100) {
			throw new RangeError('Progress value is out of range [0..100].');
		}
		switch (progress) {
			case 0:
				this.startTime = new Date();
				break;
			case 100:
				this.finishTime = new Date();
				break;
			default:
				this.finishTime = undefined;
				break;
		}
		if (this._progress !== progress) {
			this._progress = progress;
			this.events.progressUpdated.execute(progress);
		}
	}

	public get isExpired(): boolean {
		const expired = this._canceled || this._failed;
		if (this.finishTime && !expired) {
			return Date.now() - this.finishTime.getTime() >= this.timeout;
		}
		return expired;
	}

	public async cancel(): Promise<void> {
		const cancel = this.cancelable && (await this.events.processCanceling.execute()).every(Boolean);
		if (cancel) {
			this._progress = 0;
			this._canceled = true;
			await this.events.processCanceled.execute();
		}
	}

	public async fail(): Promise<void> {
		this._progress = 0;
		this._failed = true;
		await this.events.processFailed.execute();
	}

	public onProgressUpdated(handler: EventHandler<number>) {
		return this.events.progressUpdated.add(handler);
	}

	public onProcessStatusUpdated(handler: EventHandler<OperationStatus>) {
		return this.events.processStatusUpdated.add(handler);
	}

	public onProcessCanceling(handler: EventHandler<void, Promise<boolean>>) {
		return this.events.processCanceling.add(handler);
	}

	public onProcessCanceled(handler: EventHandler) {
		return this.events.processCanceled.add(handler);
	}

	public onProcessFailed(handler: EventHandler) {
		return this.events.processFailed.add(handler);
	}
}
